/**
 * The catalogue drawer's filter rail.
 *
 * It is laid out as the request it makes. Controls in QUERY carry the exact param they write and
 * narrow the whole catalogue; the live query string underneath is the request verbatim, copyable.
 * Controls in SIFT have no param — products-api cannot filter on clarity, colour grade, lab-grown
 * or certification — so they only refine the rows already loaded, and the rail says so instead of
 * implying otherwise. The station hints carry the funnel: N in catalogue → N loaded → N shown.
 */
import { useState, type ReactNode } from "react";
import {
  AUDIENCES,
  CERT_LABS,
  CLARITIES,
  COLOUR_GRADES,
  COLOURS,
  CUTS,
  GEMSTONE_TYPES,
  KNOWN_METALS,
  RING_PRODUCT_TYPES,
  STOCK_STATUSES,
  STONE_SOURCES,
  enumLabel,
  metalDisplay,
} from "./enums";
import {
  countSift,
  countWire,
  diamondOriginApplies,
  emptySift,
  emptyWire,
  queryLine,
  type PageOptions,
  type SiftFilters,
  type SpecimenKind,
  type WireFilters,
} from "./filters";

const cx = (...c: Array<string | false | null | undefined>): string => c.filter(Boolean).join(" ");

const toggle = (arr: readonly string[], v: string): string[] =>
  arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];

/** A collapsible group. Opens itself when it already has a selection, so an active filter is never
 *  hidden behind a closed summary. */
function Group({
  name,
  wire,
  count,
  children,
  defaultOpen = false,
}: {
  name: string;
  wire?: string;
  count: number;
  children: ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen || count > 0);
  return (
    <div className={cx("f-group", open && "open")}>
      <button type="button" className="f-head" onClick={() => setOpen(!open)} aria-expanded={open}>
        <span className="f-name">{name}</span>
        {wire ? <span className="f-wire">{wire}</span> : <span className="f-local">on screen</span>}
        {count > 0 && <span className="f-count">{count}</span>}
        <span className="f-chev">›</span>
      </button>
      {open && <div className="f-body">{children}</div>}
    </div>
  );
}

/** Multi-select as wrapping checkbox chips. Values are shown verbatim: on a test bench the wire
 *  value is the useful one, and not inventing 120 labels removes a whole class of drift. */
function Chips({
  options,
  selected,
  onChange,
}: {
  options: readonly string[];
  selected: string[];
  onChange: (v: string[]) => void;
}) {
  return (
    <div className="f-chips">
      {options.map((o) => {
        const on = selected.includes(o);
        return (
          <label key={o} className={cx("f-chip", on && "on")} title={enumLabel(o)}>
            <input type="checkbox" checked={on} onChange={() => onChange(toggle(selected, o))} />
            {o}
          </label>
        );
      })}
    </div>
  );
}

/** Metals keep the bench's bead picker rather than becoming chips — station 03 already teaches that
 *  a metal is chosen by seeing it, and the two selectors should agree. */
function MetalChips({
  selected,
  onChange,
}: {
  selected: string[];
  onChange: (v: string[]) => void;
}) {
  return (
    <div className="f-metals">
      {KNOWN_METALS.map((m) => {
        const d = metalDisplay(m);
        const on = selected.includes(m);
        return (
          <label key={m} className={cx("f-metal", on && "on")} title={m}>
            <input type="checkbox" checked={on} onChange={() => onChange(toggle(selected, m))} />
            <span className={cx("bead", d.swatchClass)} />
            <span className="fam">{d.family}</span>
            <span className="kt">{d.karat}</span>
          </label>
        );
      })}
    </div>
  );
}

/** `label` is the accessible name — the group heading beside it is not associated with the select,
 *  so without this a screen reader announces an unnamed combo box. */
function Choice({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<[string, string]>;
}) {
  return (
    <select
      className="input f-select"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label={label}
    >
      {options.map(([v, l]) => (
        <option key={v} value={v}>
          {l}
        </option>
      ))}
    </select>
  );
}

function Range({
  min,
  max,
  onMin,
  onMax,
  unit,
  step,
}: {
  min: string;
  max: string;
  onMin: (v: string) => void;
  onMax: (v: string) => void;
  unit: string;
  step: string;
}) {
  return (
    <div className="f-range">
      <input
        className="input"
        type="number"
        min="0"
        step={step}
        placeholder="min"
        value={min}
        onChange={(e) => onMin(e.target.value)}
        aria-label={`minimum ${unit}`}
      />
      <span className="f-dash">–</span>
      <input
        className="input"
        type="number"
        min="0"
        step={step}
        placeholder="max"
        value={max}
        onChange={(e) => onMax(e.target.value)}
        aria-label={`maximum ${unit}`}
      />
      <span className="f-unit">{unit}</span>
    </div>
  );
}

export function FilterRail({
  kind,
  wire,
  onWire,
  sift,
  onSift,
  total,
  loaded,
  shown,
  page,
}: {
  kind: SpecimenKind;
  wire: WireFilters;
  onWire: (w: WireFilters) => void;
  sift: SiftFilters;
  onSift: (s: SiftFilters) => void;
  total: number;
  loaded: number;
  shown: number;
  page: PageOptions;
}) {
  const [copied, setCopied] = useState(false);
  const set = <K extends keyof WireFilters>(k: K, v: WireFilters[K]): void =>
    onWire({ ...wire, [k]: v });
  const setS = <K extends keyof SiftFilters>(k: K, v: SiftFilters[K]): void =>
    onSift({ ...sift, [k]: v });
  const line = queryLine(kind, wire, page);
  const nWire = countWire(kind, wire);
  const nSift = countSift(kind, sift);
  const originOpen = diamondOriginApplies(wire.stones);

  const copy = (): void => {
    void navigator.clipboard.writeText(line);
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };

  return (
    <aside className="cat-rail">
      <div className="cat-railscroll">
        <div className="lab-station">
          <span>Query</span>
          <span className="hint">{total.toLocaleString()} in catalogue</span>
        </div>
        <p className="f-note">
          Narrows the whole catalogue. Each control names the param it sends.
        </p>

        {kind === "ring" ? (
          <>
            <Group name="Ring type" wire="productType" count={wire.productTypes.length} defaultOpen>
              <Chips
                options={RING_PRODUCT_TYPES}
                selected={wire.productTypes}
                onChange={(v) => set("productTypes", v)}
              />
              <p className="f-hint">Empty means all five — wedding bands included.</p>
            </Group>
            {/* Not default-open: twelve bead tiles is four rows, and it buries everything under it. */}
            <Group name="Metal" wire="defaultMetal" count={wire.defaultMetal.length}>
              <MetalChips selected={wire.defaultMetal} onChange={(v) => set("defaultMetal", v)} />
            </Group>
            <Group name="Centre stone" wire="centerStone" count={wire.centreStone ? 1 : 0}>
              <Choice
                label="Centre stone"
                value={wire.centreStone}
                onChange={(v) => set("centreStone", v)}
                options={[
                  ["", "Any stone type"],
                  ...GEMSTONE_TYPES.map((g) => [g, enumLabel(g)] as [string, string]),
                ]}
              />
            </Group>
          </>
        ) : (
          <>
            <Group name="Stone type" wire="stones" count={wire.stones.length} defaultOpen>
              <Chips
                options={GEMSTONE_TYPES}
                selected={wire.stones}
                onChange={(v) => set("stones", v)}
              />
            </Group>
            <Group
              name="Diamond origin"
              wire="diamondOrigin"
              count={wire.diamondOrigin && originOpen ? 1 : 0}
            >
              <Choice
                label="Diamond origin"
                value={wire.diamondOrigin}
                onChange={(v) => set("diamondOrigin", v)}
                options={[
                  ["", "Any origin"],
                  ["NATURAL", "Natural"],
                  ["LAB_GROWN", "Lab-grown"],
                ]}
              />
              {!originOpen && (
                <p className="f-warn">
                  products-api only applies this when the stone type is unset or includes DIAMOND —
                  it would be dropped, so it isn’t sent.
                </p>
              )}
            </Group>
          </>
        )}

        <Group name="Cut" wire="cut" count={wire.cut.length}>
          <Chips options={CUTS} selected={wire.cut} onChange={(v) => set("cut", v)} />
        </Group>

        <Group
          name="Carat"
          wire="carat"
          count={wire.caratMin || wire.caratMax ? 1 : 0}
          defaultOpen={kind === "stone"}
        >
          <Range
            min={wire.caratMin}
            max={wire.caratMax}
            onMin={(v) => set("caratMin", v)}
            onMax={(v) => set("caratMax", v)}
            unit="ct"
            step="0.05"
          />
        </Group>

        <Group name="Colour" wire="colours" count={wire.colours.length}>
          <Chips options={COLOURS} selected={wire.colours} onChange={(v) => set("colours", v)} />
          <p className="f-hint">The fancy-colour branch. Nivoda stones never carry these.</p>
        </Group>

        <Group name="Price" wire="price" count={wire.priceMin || wire.priceMax ? 1 : 0}>
          <Range
            min={wire.priceMin}
            max={wire.priceMax}
            onMin={(v) => set("priceMin", v)}
            onMax={(v) => set("priceMax", v)}
            unit="£"
            step="50"
          />
          <p className="f-hint">Whole pounds — products-api truncates the decimals.</p>
        </Group>

        <Group name="Audience" wire="audience" count={wire.audience.length}>
          <Chips
            options={AUDIENCES}
            selected={wire.audience}
            onChange={(v) => set("audience", v)}
          />
        </Group>

        <Group
          name="Stock"
          wire="stockStatus"
          count={wire.stockStatus && wire.stockStatus !== "ALL" ? 1 : 0}
        >
          <Choice
            label="Stock"
            value={wire.stockStatus}
            onChange={(v) => set("stockStatus", v)}
            options={[
              ["", "Any"],
              ...STOCK_STATUSES.map((s) => [s, enumLabel(s)] as [string, string]),
            ]}
          />
        </Group>

        <div className="f-toggles">
          <label className="lab-check">
            <input
              type="checkbox"
              checked={wire.withImages}
              onChange={(e) => set("withImages", e.target.checked)}
            />
            Has photos <span className="f-wire">productsWithImages</span>
          </label>
          {kind === "stone" && (
            <label className="lab-check">
              <input
                type="checkbox"
                checked={wire.includeNivoda}
                onChange={(e) => set("includeNivoda", e.target.checked)}
              />
              Include Nivoda offers <span className="f-wire">includeNivoda</span>
            </label>
          )}
        </div>

        <div className="lab-station f-siftstation">
          <span>Sift</span>
          <span className="hint">
            {shown.toLocaleString()} of {loaded.toLocaleString()} loaded
          </span>
        </div>
        <p className="f-note">
          products-api has no query for these, so they refine the rows already loaded — not the
          catalogue.
        </p>

        <div className="f-sift">
          <Group name="Clarity" count={sift.clarity.length}>
            <Chips
              options={CLARITIES}
              selected={sift.clarity}
              onChange={(v) => setS("clarity", v)}
            />
          </Group>
          <Group name="Colour grade" count={sift.colourGrade.length}>
            <Chips
              options={COLOUR_GRADES}
              selected={sift.colourGrade}
              onChange={(v) => setS("colourGrade", v)}
            />
          </Group>
          <Group name="Origin" count={sift.labGrown ? 1 : 0}>
            <Choice
              label="Origin"
              value={sift.labGrown}
              onChange={(v) => setS("labGrown", v)}
              options={[
                ["", "Any"],
                ["natural", "Natural"],
                ["lab", "Lab-grown"],
              ]}
            />
          </Group>
          <Group name="Source" count={sift.source.length}>
            <Chips
              options={STONE_SOURCES}
              selected={sift.source}
              onChange={(v) => setS("source", v)}
            />
            <p className="f-hint">
              Own stock never carries certification; Nivoda never carries fancy colours.
            </p>
          </Group>
          <Group name="Certification" count={sift.certLab.length + (sift.certified ? 1 : 0)}>
            <Choice
              label="Certified"
              value={sift.certified}
              onChange={(v) => setS("certified", v)}
              options={[
                ["", "Certified or not"],
                ["yes", "Certified"],
                ["no", "Not certified"],
              ]}
            />
            <div className="f-gap" />
            <Chips
              options={CERT_LABS}
              selected={sift.certLab}
              onChange={(v) => setS("certLab", v)}
            />
          </Group>

          {kind === "ring" && (
            <>
              <Group name="Photos" count={sift.photos ? 1 : 0}>
                <Choice
                  label="Photos"
                  value={sift.photos}
                  onChange={(v) => setS("photos", v)}
                  options={[
                    ["", "Any number"],
                    ["multi", "More than one"],
                  ]}
                />
                <p className="f-hint">
                  More than one photo means all_images has something to fan out to.
                </p>
              </Group>
              <Group name="Baseline" count={sift.baseline ? 1 : 0}>
                <Choice
                  label="Baseline"
                  value={sift.baseline}
                  onChange={(v) => setS("baseline", v)}
                  options={[
                    ["", "Any"],
                    ["clean", "Clean"],
                    ["flagged", "Flagged"],
                  ]}
                />
                <p className="f-hint">
                  Whether a single default metal and centre stone could be read — the operation is
                  inferred from them.
                </p>
              </Group>
            </>
          )}
        </div>

        {nSift > 0 && (
          <button
            className="btn ghost small-btn f-clearsift"
            type="button"
            onClick={() => onSift(emptySift())}
          >
            Clear {nSift} sift
          </button>
        )}
      </div>

      {/* Pinned, so the request is never a scroll away from the controls that write it. This is the
          same string the drawer fetches and the same string Copy puts on the clipboard. */}
      <div className="cat-railfoot">
        <div className="lab-divlabel">the request</div>
        <pre className="f-query">{line}</pre>
        <div className="f-queryrow">
          <button className="btn small-btn" type="button" onClick={copy}>
            {copied ? "Copied!" : "Copy request"}
          </button>
          {nWire > 0 && (
            <button
              className="btn ghost small-btn"
              type="button"
              onClick={() => onWire({ ...emptyWire(kind), search: wire.search })}
            >
              Clear {nWire}
            </button>
          )}
        </div>
      </div>
    </aside>
  );
}
